import * as React from 'react';
import { connect } from 'react-redux';
import { css } from '@emotion/css';
import { theme } from '@branchjs/neo-components';

import { retrieveDiscoAdsAdGroups, saveDiscoAdsCreatorStep, updateCurrentAdGroup } from '../../../actions/DiscoAdsActions';

import {
  getAdGroupSetup,
  getAdGroupsNames,
  getCreatorNamespace,
  getCurrentCampaignIdFromShareable,
  getDateRangeInfo,
} from '../selectors/pageStateSelectors';

import DiscoAdsCreatorFormSection from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorFormSection';
import DiscoAdsCreatorViewComponentWrapper from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorViewComponentWrapper';

import {
  AD_OBJECT_SETTINGS_SAVE_BTN_COPY,
  DA_AD_GROUP_SETUP,
  DA_CAMPAIGN_CREATOR_NAMESPACE,
  DA_CREATOR_FORM_SECTION_COPY,
  STEPPER_CARD_COMPLETE_STEP,
} from '../discoAdsConstants';

import { isBidValid, formatStringBidToTwoDecimals } from '../discoAdsUtils';

import MessageBox from '../../../../common/components/core/MessageBox';

const castNumBidToString = (bid) => `$${bid.toFixed(2)}`;

const getAdGroupSettings = (adGroupSettings) => {
  const { name, default_max_cpc } = adGroupSettings;
  const defaultBid = default_max_cpc !== undefined ? castNumBidToString(default_max_cpc) : '';
  return {
    adGroupName: name || '',
    defaultBid,
  };
};

const getFormState = (props) => {
  const { adGroupSettings, adGroupSetup: createFlowAdGroupSetupData } = props;
  const { adGroupName, defaultBid } = createFlowAdGroupSetupData;
  let state = {};
  if (adGroupSettings) {
    state = getAdGroupSettings(adGroupSettings);
  } else {
    state = {
      adGroupName: adGroupName || '',
      defaultBid: defaultBid || '',
    };
  }
  return Object.assign(state, {
    defaultBidBorderColor: css({ borderColor: 'none' }),
  });
};

class DiscoAdsCreateAdGroup extends React.Component {
  state = getFormState(this.props);

  componentDidUpdate(prevProps) {
    const { adGroupSettings, onCancel: onOpenCloseSettingsModal } = this.props;
    if (prevProps.adGroupSettings !== adGroupSettings) {
      onOpenCloseSettingsModal();
    }
  }

  componentDidMount() {
    const {
      campaignId,
      dateRangeInfo: { dateRange },
    } = this.props;

    if (campaignId) {
      this.props.retrieveDiscoAdsAdGroups({ campaignId, dateRange });
    }
  }

  _onAdGroupNameInputChange = (e) => {
    const { value: adGroupName } = e.target;
    this.setState({
      adGroupName,
    });
  };

  _onDefaultBidChange = (e) => {
    const { value: defaultBid } = e.target;
    if (defaultBid === '$') {
      return;
    }
    if (defaultBid[1] === '$') {
      this.setState({ defaultBid: '$' });
      return;
    }
    const bidWithoutDollarSign = defaultBid.slice(1);
    const bidIsValid = isBidValid(bidWithoutDollarSign);
    const borderColor = `${bidIsValid ? 'none' : `${theme.colors.error.main} !important`}`;
    this.setState({
      defaultBid: `$${bidWithoutDollarSign}`,
      defaultBidBorderColor: css({ borderColor }),
    });
  };

  _onDefaultBidInputFocus = () => {
    const { defaultBid } = this.state;
    if (!defaultBid.length) {
      this.setState({
        defaultBid: '$',
      });
    }
  };

  _onDefaultBidInputBlur = (e) => {
    const { value } = e.target;
    if (value === '$') {
      this.setState({
        defaultBid: '',
        defaultBidBorderColor: css({ borderColor: 'none' }),
      });
    }
  };

  _defaultBidIsComplete = () => {
    const { defaultBid } = this.state;
    const bidWithoutDollarSign = defaultBid.slice(1);
    return bidWithoutDollarSign.length > 0 && isBidValid(bidWithoutDollarSign);
  };

  _allFieldsArecomplete = () => {
    const { adGroupName } = this.state;
    const defaultBidIsComplete = this._defaultBidIsComplete();
    return (
      !!adGroupName.length && (this._isInCreateCampaignForm() || !this._isAdGroupNameDuplicated()) && defaultBidIsComplete
    );
  };

  _onSaveAdGroupSettings = () => {
    const { updateCurrentAdGroup, adGroupSettings } = this.props;
    const { id } = adGroupSettings;
    const { adGroupName: name, defaultBid } = this.state;
    const bidWithoutDollarSign = defaultBid.slice(1);
    const bidToTwoDecimals = formatStringBidToTwoDecimals(bidWithoutDollarSign);
    const payload = { id, name, default_max_cpc: bidToTwoDecimals };
    updateCurrentAdGroup(payload);
  };

  _extractAdGroupSetupDataFromState = () => {
    const { adGroupName, defaultBid } = this.state;
    return {
      adGroupName,
      defaultBid,
    };
  };

  _onSaveAndContinueClick = () => {
    const { onCompletionStatusChange, onNext, saveDiscoAdsCreatorStep } = this.props;
    const payload = this._extractAdGroupSetupDataFromState();
    saveDiscoAdsCreatorStep({ stepKey: DA_AD_GROUP_SETUP, payload });
    onCompletionStatusChange(STEPPER_CARD_COMPLETE_STEP);
    onNext();
  };

  _handleSave = () => {
    if (this.props.adGroupSettings) {
      return this._onSaveAdGroupSettings();
    }
    this._onSaveAndContinueClick();
  };

  _isAdGroupNameDuplicated = () => {
    const { allAdGroupsNames, adGroupSettings } = this.props;
    const { adGroupName } = this.state;
    const initialAdGroupName = adGroupSettings?.name;

    return adGroupName && adGroupName !== initialAdGroupName && allAdGroupsNames.indexOf(adGroupName) > -1;
  };

  _isInCreateCampaignForm = () => this.props.creatorNamespace === DA_CAMPAIGN_CREATOR_NAMESPACE;

  render() {
    const {
      adGroup: { name: adGroupNameCopy, defaultBid: defaultBidCopy },
    } = DA_CREATOR_FORM_SECTION_COPY;
    const { adGroupName, defaultBid, defaultBidBorderColor } = this.state;
    const defaultBidInputStyles = css(defaultBidBorderColor);

    const adGroupNameDuplicated = !this._isInCreateCampaignForm() && this._isAdGroupNameDuplicated();

    return (
      <DiscoAdsCreatorViewComponentWrapper
        saveAndContinueButtonIsDisabled={!this._allFieldsArecomplete()}
        onSaveClick={this._handleSave}
        onCancelClick={this.props.onCancel}
        saveButtonCopy={this.props.adGroupSettings && AD_OBJECT_SETTINGS_SAVE_BTN_COPY}
      >
        <DiscoAdsCreatorFormSection
          title={adGroupNameCopy.title}
          subtitle={adGroupNameCopy.subtitle}
          inputPlaceholder="eg. Competitive Ad Group"
          inputValue={adGroupName}
          onInputChange={this._onAdGroupNameInputChange}
          testid="dac-ad-group-name"
        />
        {adGroupNameDuplicated && (
          <MessageBox
            type="error"
            noTitle
            text={[`An ad group with “${adGroupName}” name already exists.`, 'Please enter another name.']}
          />
        )}

        <DiscoAdsCreatorFormSection
          title={defaultBidCopy.title}
          subtitle={defaultBidCopy.subtitle}
          inputPlaceholder="eg. $5.00"
          inputValue={defaultBid}
          inputClassName={defaultBidInputStyles}
          onInputChange={this._onDefaultBidChange}
          onInputFocus={this._onDefaultBidInputFocus}
          onInputBlur={this._onDefaultBidInputBlur}
          testid="dac-ad-group-default-max-cpc"
        />
      </DiscoAdsCreatorViewComponentWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  adGroupSetup: getAdGroupSetup(state),
  allAdGroupsNames: getAdGroupsNames(state),
  campaignId: getCurrentCampaignIdFromShareable(state),
  creatorNamespace: getCreatorNamespace(state),
  dateRangeInfo: getDateRangeInfo(state),
});

const mapDispatchToProps = (dispatch) => {
  return {
    saveDiscoAdsCreatorStep: ({ stepKey, payload }) => {
      dispatch(saveDiscoAdsCreatorStep({ stepKey, payload }));
    },
    updateCurrentAdGroup: (payload) => {
      dispatch(updateCurrentAdGroup(payload));
    },
    retrieveDiscoAdsAdGroups: ({ campaignId, dateRange }) => {
      dispatch(retrieveDiscoAdsAdGroups({ campaignId, dateRange }));
    },
  };
};

const ConnectedDiscoAdsCreateAdGroup = connect(mapStateToProps, mapDispatchToProps)(DiscoAdsCreateAdGroup);

export default ConnectedDiscoAdsCreateAdGroup;
